import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { RotateCw, ZoomIn, Maximize2 } from "lucide-react";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { EXPLORATION_VIEWS } from "../../lib/product";
import { SectionIntro, DemoChip } from "./shared";

const ROOMS = ["LIVING", "KITCHEN", "BEDROOMS", "BATHROOMS"];

const ArchitecturalExploration3D = () => {
    const [active, setActive] = useState("exterior");
    const reduce = useReducedMotion();
    const current = EXPLORATION_VIEWS.find((v) => v.id === active);

    return (
        <section
            id="exploration"
            data-testid="architectural-3d-exploration-section"
            className="relative bg-surface/40 py-24 lg:py-32"
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                <SectionIntro
                    testid="exploration-intro"
                    eyebrow="3D ARCHITECTURAL EXPLORATION"
                    title="Explore the house before the house exists."
                    copy="Move through the design in plan, model and realistic form — exterior, interior and floor by floor."
                />

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    data-testid="exploration-interface"
                    className="mt-14 border border-white/12 bg-[#0C1428]"
                >
                    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
                        <div
                            role="group"
                            aria-label="Interface mode (staged capability)"
                            className="flex items-center gap-4 font-mono text-[13px] tracking-[0.22em]"
                        >
                            {["PLAN", "3D", "REALISTIC"].map((mode, i) => (
                                <span
                                    key={mode}
                                    className={
                                        i === 1
                                            ? "border-b border-accent pb-1 text-accent"
                                            : "pb-1 text-cad"
                                    }
                                >
                                    {mode}
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 font-mono text-[13px] tracking-[0.18em] text-mist">
                            <span className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1.5">
                                <RotateCw className="h-3 w-3" /> ORBIT
                            </span>
                            <span className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1.5">
                                <ZoomIn className="h-3 w-3" /> ZOOM
                            </span>
                            <span className="hidden items-center gap-1.5 border border-white/15 px-2.5 py-1.5 sm:inline-flex">
                                <Maximize2 className="h-3 w-3" /> FULLSCREEN
                            </span>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-[200px_1fr]">
                        <div className="flex gap-2 overflow-x-auto border-b border-white/10 px-5 py-4 lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r">
                            {EXPLORATION_VIEWS.map((v) => (
                                <button
                                    key={v.id}
                                    type="button"
                                    data-testid={`exploration-view-${v.id}`}
                                    aria-pressed={active === v.id}
                                    onClick={() => setActive(v.id)}
                                    className={`shrink-0 px-4 py-2 text-left text-[14px] transition-colors duration-300 ${
                                        active === v.id
                                            ? "bg-accent/10 font-semibold text-accent"
                                            : "text-mist hover:text-white"
                                    }`}
                                >
                                    {v.label}
                                </button>
                            ))}
                            <span className="hidden shrink-0 px-4 py-2 text-[14px] text-cad lg:block">
                                Materials
                            </span>
                            <span className="hidden shrink-0 px-4 py-2 text-[14px] text-cad lg:block">
                                Views
                            </span>
                        </div>

                        <div className="relative">
                            <div className="relative aspect-[16/10] overflow-hidden">
                                <motion.img
                                    key={current.img}
                                    src={current.img}
                                    alt={`${current.label} view of the home in the exploration interface`}
                                    initial={{ opacity: reduce ? 1 : 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.6 }}
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                                <span className="absolute left-4 top-4 flex items-center gap-2">
                                    <DemoChip>STAGED CAPABILITY</DemoChip>
                                    <DemoChip>UI PREVIEW</DemoChip>
                                </span>
                                <span className="absolute bottom-4 right-4 font-mono text-[13px] tracking-[0.22em] text-white/70">
                                    SCALE 1:100
                                </span>
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                                <div className="flex flex-wrap gap-2">
                                    {ROOMS.map((room) => (
                                        <span
                                            key={room}
                                            className="border border-white/15 px-3 py-1.5 font-mono text-[13px] tracking-[0.22em] text-mist"
                                        >
                                            {room}
                                        </span>
                                    ))}
                                </div>
                                <p className="font-mono text-[13px] leading-relaxed tracking-[0.18em] text-cad">
                                    EXPLORATION CONTROLS SHOWN AS PRODUCT
                                    DIRECTION — INTERACTIVE 3D IS IN
                                    DEVELOPMENT
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ArchitecturalExploration3D;
